import { ref, uploadBytes } from "firebase/storage";
import { toLower, orderBy as lodashOrderBy } from "lodash";
import { v4 as uuid } from 'uuid';
import { isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isNumberGreaterThanOrEqualToZero, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from "../utils/objectUtils";
import { convertToSlug } from "../utils/stringFormatter";
import { addValueToChildArray, create, getAll, getById, getByIdQuery, getBySingleQuery, remove, update, updateField, updateFields } from '../utils/dbUtils';
import { storage, db } from "../firebase";
import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from "firebase/firestore";
import { normalizeSurveyFlowItems, validateUniqueSurveyFlowKeys } from "./SurveyFlowItem";

class Program {
    static collectionName = 'programs';

    id = null;
    name = null;
    slug = convertToSlug(this.name);

    status = "PUBLISHED"; // Enum ProductStatuses

    productIds = []; // array of productIds
    categoryIds = []; // array of productCategoryId

    #images = [];
    imageRefIds = [];

    metaData = []; // array of product meta data

    surveys = []; // array of SurveyFlowItem

    programFee = 0.0; // per renewal fee for the program itself

    detailSections = []; // array of ProductMetaData - for ShippingPolicy, RefundPolicy, Specifications, etc.

    defaultCampaignId = null;
    duplicateEnrollmentGroupId = null;
    duplicateCheckScope = 'NONE'; // Enum DuplicateCheckScopes
    duplicateCheckMode = null; // Enum DuplicateCheckModes

    constructor(program) {
        if (isObjectNotEmpty(program)) {
            if (isStringNotEmpty(program.id)) { this.id = program.id; }
            if (isStringNotEmpty(program.name)) { this.name = program.name; }
            if (isStringNotEmpty(program.slug)) { this.slug = program.slug; }
            else { this.slug = convertToSlug(this.name); }

            if (isStringNotEmpty(program.status)) { this.status = program.status; }

            if (isListNotEmpty(program.productIds)) { this.productIds = program.productIds; }
            if (isListNotEmpty(program.categoryIds)) { this.categoryIds = program.categoryIds; }

            if (isListNotEmpty(program.images)) { this.#images = program.images; }
            if (isListNotEmpty(program.imageRefIds)) { this.imageRefIds = program.imageRefIds; }

            if (isListNotEmpty(program.metaData)) { this.metaData = program.metaData; }

            if (isListNotEmpty(program.surveys)) { this.surveys = normalizeSurveyFlowItems(program.surveys); }

            if (isNumberGreaterThanOrEqualToZero(program.programFee)) { this.programFee = program.programFee; }

            if (isListNotEmpty(program.detailSections)) { this.detailSections = program.detailSections; }

            if (isStringNotEmpty(program.defaultCampaignId)) { this.defaultCampaignId = program.defaultCampaignId; }
            if (isStringNotEmpty(program.duplicateEnrollmentGroupId)) { this.duplicateEnrollmentGroupId = program.duplicateEnrollmentGroupId; }
            if (isStringNotEmpty(program.duplicateCheckScope)) { this.duplicateCheckScope = program.duplicateCheckScope; }
            if (isStringNotEmpty(program.duplicateCheckMode)) { this.duplicateCheckMode = program.duplicateCheckMode; }
        }
    }

    toDto = () => {
        return {
            name: this.name,
            slug: this.slug,

            status: this.status,

            productIds: this.productIds,
            categoryIds: this.categoryIds,

            imageRefIds: this.imageRefIds,

            metaData: this.metaData,

            surveys: this.surveys,

            programFee: this.programFee,

            detailSections: this.detailSections,

            defaultCampaignId: this.defaultCampaignId,
            duplicateEnrollmentGroupId: this.duplicateEnrollmentGroupId,
            duplicateCheckScope: this.duplicateCheckScope,
            duplicateCheckMode: this.duplicateCheckMode,
        };
    }

    isValid = () => {
        if (isStringEmpty(this.name)) {
            throw new Error('Name is required.');
        }
        validateUniqueSurveyFlowKeys(this.surveys, 'Program surveys');

        return true;
    }

    static addFile = async (file) => {
        if (isNotNullOrUndefined(file)) {
            try {
                const fileName = uuid() + '.' + Program.getFileExt(file);
                const storageRef = ref(storage, 'programs/' + fileName);

                return await uploadBytes(storageRef, file);
            } catch (err) {
                console.log(err);
            }
        }

        return null;
    }

    static getFileExt = (file) => {
        const lastDot = file.name.lastIndexOf('.');
        //const fileName = fullFileName.substring(0, lastDot);
        const ext = file.name.substring(lastDot + 1);

        return toLower(ext);
    }

    static uploadImages = async (files) => {
        let fileRefIds = [];
        if (isListNotEmpty(files)) {
            for (const file of files) {
                const uploadRes = await Program.addFile(file);
                // console.log(uploadRes);
                if (isObjectNotEmpty(uploadRes)) {
                    fileRefIds.push(uploadRes.ref.fullPath);
                }
            }
        }

        return fileRefIds;
    }

    static addImage = async (id, file) => {
        if (isNotNullOrUndefined(file)) {
            const uploadRes = await Program.addFile(file);
            // console.log(uploadRes);
            if (isObjectNotEmpty(uploadRes)) {
                return await addValueToChildArray(Program.collectionName, id, 'imageRefIds', uploadRes.ref.fullPath);
            }
        }

        return false;
    }

    create = async () => {
        if (this.isValid()) {
            this.imageRefIds = await Program.uploadImages(isListNotEmpty(this.#images) ? [...this.#images] : []);

            return await create(Program.collectionName, this.toDto());
        }
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(Program.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static updateFields = async (id, updatedFields) => {
        if (isStringNotEmpty(id)) {
            return await updateFields(Program.collectionName, id, updatedFields);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedProgram, oldObj) => {
        const updatedObj = new Program(updatedProgram);
        if (updatedObj.isValid() && id === updatedProgram.id) {
            if (isListNotEmpty(updatedObj.#images)) {
                const imageRefIds = await Program.uploadImages(updatedObj.#images);
                updatedObj.imageRefIds = isListNotEmpty(updatedObj.imageRefIds) ? [...updatedObj.imageRefIds, ...imageRefIds] : [...imageRefIds];
            }

            const success = await update(Program.collectionName, id, updatedObj.toDto());
            if (isBooleanTrue(success)) {
                return { ...oldObj, ...updatedObj };
            }
        } else {
            throw new Error('id and id field of the updated program must match.')
        }

        return null;
    }

    static updateImages = async (id, images, oldProgram) => {
        let imageRefIds = [];
        if (isListNotEmpty(images)) {
            const orderedImages = lodashOrderBy(images, ['order'], ['asc']);
            // console.log('orderedImages', orderedImages);
            for (const image of orderedImages) {
                if (isNotNullOrUndefined(image.file)) {
                    const uploadRes = await Program.addFile(image.file);
                    // console.log(uploadRes);
                    if (isObjectNotEmpty(uploadRes)) {
                        imageRefIds.push(uploadRes.ref.fullPath);
                    }
                } else if (isStringNotEmpty(image.fileRefId)) {
                    imageRefIds.push(image.fileRefId);
                }
            }
        }
        // console.log('imageRefIds', imageRefIds);

        const success = await update(Program.collectionName, id, { imageRefIds: imageRefIds });
        if (isBooleanTrue(success)) {
            return { ...oldProgram, imageRefIds: imageRefIds };
        }

        return null;
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(Program.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(Program.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getAll = async () => {
        return await getAll(Program.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(Program.collectionName, fieldName, fieldValue, comparer);
    }

    static getByProductId = async (productId) => {
        return await getBySingleQuery(Program.collectionName, 'productIds', productId, 'in');
    }

    static getByCategoryId = async (categoryId) => {
        return await getBySingleQuery(Program.collectionName, 'categoryIds', categoryId, 'in');
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(Program.collectionName, ids, 'in');
    }

    static getCount = async (queryParameters) => {
        const { productId, categoryId, status } = queryParameters;

        let queryConstraints = [];

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isListNotEmpty(productId)) {
            queryConstraints.push(where('productId', 'array-contains-any', productId));
        } else if (isStringNotEmptyOrZeroLength(productId)) {
            queryConstraints.push(where('productId', 'array-contains', productId));
        }

        if (isListNotEmpty(categoryId)) {
            queryConstraints.push(where('categoryIds', 'array-contains-any', categoryId));
        } else if (isStringNotEmptyOrZeroLength(categoryId)) {
            queryConstraints.push(where('categoryIds', 'array-contains', categoryId));
        }

        let q = query(collection(db, Program.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        //console.log(snapShot.data());

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'name') => {
        const { productId, categoryId, status } = queryParameters;

        let queryConstraints = [];

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isListNotEmpty(productId)) {
            queryConstraints.push(where('productId', 'array-contains-any', productId));
        } else if (isStringNotEmptyOrZeroLength(productId)) {
            queryConstraints.push(where('productId', 'array-contains', productId));
        }

        if (isListNotEmpty(categoryId)) {
            queryConstraints.push(where('categoryIds', 'array-contains-any', categoryId));
        } else if (isStringNotEmptyOrZeroLength(categoryId)) {
            queryConstraints.push(where('categoryIds', 'array-contains', categoryId));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, Program.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, Program.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, Program.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        let snapShot = await getDocs(q);
        return snapShot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters) => {
        let allRecords = [];
        try {
            let next = null;
            let totalPages = 0;

            let totalCount = await Program.getCount(queryParameters);
            totalPages = Math.ceil(totalCount / 10000.0);
            // console.log('totalCount: ' + totalCount);
            // console.log('totalPages: ' + totalPages);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                // console.log('starting page ' + page);

                let list = await Program.getPaginated(queryParameters, 10000, null, next, 'name');

                // console.log(list.length);
                allRecords = [...allRecords, ...list];

                // console.log(allRecords.length);

                // console.log('go to next page');
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }
};

export default Program;
